/**
 * Spotify Error Page
 * Shown when Spotify OAuth fails (e.g., user not in Developer Dashboard)
 */

import { useSearchParams, Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, Mail, ArrowLeft, Copy, Check } from 'lucide-react';
import { useState } from 'react';

interface ErrorInfo {
  title: string;
  description: string;
  isDeveloperDashboardIssue: boolean;
}

const ERROR_INFO: Record<string, ErrorInfo> = {
  access_denied: {
    title: 'Access Denied',
    description:
      'Your Spotify account is not authorized to use this app. Splister is currently in Development mode, which means only pre-approved accounts can log in.',
    isDeveloperDashboardIssue: true,
  },
  unauthorized_client: {
    title: 'App Not Configured',
    description:
      'The Splister app is not properly configured in Spotify. This is an administrator issue.',
    isDeveloperDashboardIssue: false,
  },
  invalid_scope: {
    title: 'Invalid Permissions',
    description:
      'The app requested permissions that are not available. This is an administrator issue.',
    isDeveloperDashboardIssue: false,
  },
  server_error: {
    title: 'Spotify Server Error',
    description:
      'Spotify encountered an error. Please try again later.',
    isDeveloperDashboardIssue: false,
  },
};

export function SpotifyErrorPage() {
  const [searchParams] = useSearchParams();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const errorCode = searchParams.get('error') || 'unknown';
  const errorDescription = searchParams.get('error_description');

  const errorInfo = ERROR_INFO[errorCode] || {
    title: 'Authentication Failed',
    description: errorDescription || 'An unknown error occurred during Spotify authentication.',
    isDeveloperDashboardIssue: false,
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const adminEmail = 'mhavo@iki.fi';
  const mailtoLink = `mailto:${adminEmail}?subject=Splister Access Request&body=Hi,%0A%0AI would like to request access to Splister.%0A%0A1. My Spotify User ID: [paste your username here]%0A2. My Email: [paste your Spotify email here]%0A%0AThank you!`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-6xl font-bold text-white mb-2">Splister</h1>
          <p className="text-gray-400 text-lg">Smart Playlist Management for Spotify</p>
        </div>

        {/* Main Card */}
        <div className="bg-gray-800 rounded-lg shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border-b border-red-600/30 px-8 py-6">
            <div className="flex items-center gap-4">
              <div className="bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">{errorInfo.title}</h2>
                <p className="text-red-200/80 font-mono text-sm">error: {errorCode}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-6 space-y-6">
            {/* Explanation */}
            <div className="text-gray-300">
              <p>{errorInfo.description}</p>
            </div>

            {/* Developer Dashboard Issue - Show how to find User ID */}
            {errorInfo.isDeveloperDashboardIssue && (
              <>
                {/* Why this happens */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <h3 className="text-blue-300 font-semibold mb-2">Why does this happen?</h3>
                  <p className="text-gray-300 text-sm">
                    Splister is currently in Development mode on Spotify. Only users that have been
                    manually added to the app's whitelist can log in. To get access, you need to
                    send your <strong>Spotify User ID</strong> and <strong>email address</strong> to the administrator.
                  </p>
                </div>

                {/* What you need to send */}
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <h3 className="text-amber-300 font-semibold mb-2">What you need to send</h3>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 bg-gray-800 rounded p-3 border border-gray-600">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">1. User ID</p>
                      <p className="text-gray-300 text-sm">Your Spotify username</p>
                    </div>
                    <div className="flex-1 bg-gray-800 rounded p-3 border border-gray-600">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">2. Email</p>
                      <p className="text-gray-300 text-sm">Your Spotify account email</p>
                    </div>
                  </div>
                </div>

                {/* How to find your details */}
                <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
                      How to find your details
                    </h3>
                  </div>
                  <div className="p-4 space-y-4">
                    <ol className="text-gray-300 text-sm space-y-3 list-decimal list-inside">
                      <li>
                        Open your{' '}
                        <a
                          href="https://www.spotify.com/account/profile/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
                        >
                          Spotify Account Profile
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </li>
                      <li>Log in if needed</li>
                      <li>
                        Find your <strong>Username</strong> — this is your User ID
                      </li>
                      <li>
                        Find your <strong>Email</strong> — your registered email address
                      </li>
                      <li>Copy both and send them to the administrator</li>
                    </ol>

                    {/* Visual example */}
                    <div className="bg-gray-800 rounded p-3 border border-gray-600 space-y-2">
                      <p className="text-xs text-gray-500 mb-2">Example of what you're looking for:</p>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-sm w-20">Username:</span>
                        <code className="font-mono text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded text-sm">
                          31abc123xyz456
                        </code>
                        <span className="text-gray-500 text-xs">← User ID</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-sm w-20">Email:</span>
                        <code className="font-mono text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded text-sm">
                          your.email@example.com
                        </code>
                        <span className="text-gray-500 text-xs">← Email</span>
                      </div>
                    </div>

                    {/* Alternative: Spotify Desktop App */}
                    <div className="text-gray-400 text-xs border-t border-gray-700 pt-3">
                      <strong>Alternative for User ID:</strong> In Spotify Desktop app, click your profile →
                      Share → Copy link to profile. The ID is the last part of the URL after{' '}
                      <code className="text-gray-300">/user/</code>
                    </div>
                  </div>
                </div>

                {/* Admin contact */}
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                  <h3 className="text-emerald-300 font-semibold mb-3">Request Access</h3>
                  <p className="text-gray-300 text-sm mb-4">
                    Send your <strong>User ID</strong> and <strong>email address</strong> to:
                  </p>
                  <div className="flex items-center gap-3 bg-gray-800 rounded p-3 mb-4">
                    <span className="text-gray-300 font-mono">{adminEmail}</span>
                    <button
                      onClick={() => copyToClipboard(adminEmail, 'email')}
                      className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                      title="Copy email"
                    >
                      {copiedField === 'email' ? (
                        <Check className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Non-developer issue */}
            {!errorInfo.isDeveloperDashboardIssue && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <h3 className="text-amber-300 font-semibold mb-2">What to do</h3>
                <p className="text-gray-300 text-sm">
                  This appears to be a configuration issue. Please contact the administrator at{' '}
                  <a href={`mailto:${adminEmail}`} className="text-emerald-400 hover:text-emerald-300">
                    {adminEmail}
                  </a>{' '}
                  and include this error code: <code className="text-amber-300">{errorCode}</code>
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a
                href={mailtoLink}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                <Mail className="w-5 h-5" />
                Contact Administrator
              </a>
              <Link
                to="/login"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Login
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-gray-500 text-sm">
          <p>
            Having trouble?{' '}
            <a href={`mailto:${adminEmail}`} className="text-emerald-400 hover:text-emerald-300">
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
